import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

import { isPositiveOrNegative } from 'src/components/dashboard/helper';

/**
 * Width of the bar relative to the configured value scale (`x_axis_max`): a `mean` of
 * 3.8 on a 1–5 scale fills 76% instead of the 3.8% it would fill as a percentage.
 * Values outside the scale are clamped and non-numeric ones fall back to 0, so neither
 * a misconfigured scale nor unexpected data can break the layout.
 */
const getBarWidthPercentage = (value: number, scaleMax: number) => {
  const percentage = (value / scaleMax) * 100;

  if (!Number.isFinite(percentage)) return 0;

  return Number(Math.min(Math.max(percentage, 0), 100).toFixed(2));
};

export const styles = {
  barTableWidget: (theme: Theme) => ({
    flexGrow: 1,
    maxWidth: '100%',
    p: 2,

    '.MuiTableContainer-root': {
      // These styles are required for the scrollbar to be permanently displayed (Does not work for iOS).
      '&::-webkit-scrollbar': {
        WebkitAppearance: 'none',
        '&:horizontal': {
          height: 5,
        },
      },

      // These styles are required for the scrollbar to be permanently displayed (Does not work for iOS).
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 2,
        backgroundColor: theme.palette.grey[500],
      },
    },

    '.MuiTable-root': (theme: Theme): SystemStyleObject<Theme> => ({
      borderCollapse: 'separate',

      th: {
        maxWidth: 0, // Necessary for Safari ('overflow-wrap: anywhere' can not be used in Safari.)
        height: 150,
      },

      td: {
        minWidth: 100,
      },

      'th, td': {
        borderRight: `1px solid ${theme.palette.grey[300]}`, //  Default mui border-color.
      },

      'tbody tr td': {
        borderBottom: 'none',
      },
    }),

    '.NumberDifference-root': (theme: Theme) => ({
      ...theme.typography.body2,
      display: 'flex',
      alignItems: 'center',
    }),
  }),

  firstColumn: (theme: Theme): SystemStyleObject<Theme> => ({
    background: theme.palette.common.white,
    left: 0,
    position: 'sticky',
    zIndex: 1,
    maxWidth: 100,

    [theme.breakpoints.up('sm')]: {
      minWidth: 150,
      maxWidth: 200,
    },
  }),

  firstColumnText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box', // 'WebkitBoxOrient', 'WebkitLineClamp' does not work without "display: '-webkit-box'".
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
  },

  rotateText: {
    transform: 'rotate(270deg)',
    maxHeight: 150,
    maxWidth: 100,
    margin: 'auto',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box', // 'WebkitBoxOrient', 'WebkitLineClamp' does not work without "display: '-webkit-box'".
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
    overflowWrap: 'anywhere',
  },

  bar:
    ({
      comparisonCount,
      comparisonDiff,
      scaleMax,
    }: {
      comparisonCount: number | null | undefined;
      comparisonDiff: number | null | undefined;
      scaleMax: number;
    }) =>
    (theme: Theme): SystemStyleObject<Theme> => {
      const positiveOrNegative = isPositiveOrNegative(comparisonDiff);

      return {
        backgroundColor: theme.colors.neutral[100],
        height: 22,
        display: 'flex',
        flexGrow: 1,

        '&:after': {
          backgroundColor: positiveOrNegative
            ? positiveOrNegative > 0
              ? theme.colors.differencePositive
              : theme.colors.differenceNegative
            : theme.colors.differenceNeutral,
          content: '""',
          width: comparisonCount
            ? `${getBarWidthPercentage(comparisonCount, scaleMax)}%`
            : undefined,
          display: 'block',
        },
      };
    },
};
